import { useMemo } from "react";
import { useOpsStore } from "../state/opsStore";
import "./QueueDepthSparklines.css";

/**
 * Phase 18 WU-18.D7 — Queue Depth panel.
 *
 * Renders current per-step pending-document counts from
 * opsStore.queueDepths. The full sparkline (60-min window) uses
 * `/api/analytics/queue-depth?bucket=hour` and is added in WU-18.E
 * (AnalyticsPage throughput tab). For the Ops Console, the current
 * depth alone is enough to see where the pile-up is right now.
 */
export function QueueDepthSparklines() {
  const queueDepths = useOpsStore((s) => s.queueDepths);

  const entries = useMemo(() => {
    return Object.entries(queueDepths)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [queueDepths]);

  if (entries.length === 0) {
    return (
      <div
        className="ops-queue-depth__empty"
        data-testid="ops-queue-depth-empty"
      >
        Queues are empty.
      </div>
    );
  }

  const max = Math.max(...entries.map(([, v]) => v), 1);

  return (
    <div className="ops-queue-depth" data-testid="ops-queue-depth">
      {entries.map(([step, count]) => {
        const widthPct = Math.max(4, Math.round((count / max) * 100));
        return (
          <div key={step} className="ops-queue-depth__row" data-step={step}>
            <span className="ops-queue-depth__step">{step}</span>
            <div className="ops-queue-depth__bar">
              <div
                className="ops-queue-depth__fill"
                style={{ width: `${widthPct}%` }}
              />
            </div>
            <span className="ops-queue-depth__count">{count}</span>
          </div>
        );
      })}
    </div>
  );
}
